import Axios from "axios";
import { Button } from "components";
import {
    BookingFlowNarrowContent,
    BookingFlowPageHeading,
    BookingFlowPaymentFooterDisclaimers,
    BookingFlowScreeningPayment,
} from "components/booking-flow";
import { getPublicBookingFlowDueSummary } from "utils/booking-flow-payment-due-summary";
import useAnalytics from "hooks/analytics";
import useBookingFlowRedesign from "hooks/useBookingFlowRedesign";
import useAuthUser from "hooks/auth-user";
import { FollowUpType } from "models/appointment";
import { useEffect, useState } from "react";
import { useAsyncCallback } from "react-async-hook";
import { trackScreeningPaymentSubmitted } from "utils/analytics/booking-flow-tracking";
import { buildReservedAppointmentDescription } from "utils/appointment-display";
import ENDPOINTS from "utils/endpoints";
import {
    getAppointmentHold,
    getBookingFollowUpType,
} from "utils/session-storage";
import { CardElementsProvider } from "context/stripe-provider";
import useLanguage from "../../../../hooks/language";
import PaymentFieldComponent from "../payment-field/payment-field";
import ExpressWalletCheckout, {
    WalletCheckoutFlow,
} from "../payment-field/express-wallet-checkout";
import PaymentMethodsBusy from "../payment-field/payment-methods-busy";
import useCardPlaceholder from "../payment-field/use-card-placeholder";
import useCardToken from "../payment-field/use-card-token";
import { ScreeningPaymentType } from "../payment-types";
import ServiceSelect from "./service-select";
import styles from "./upfront-payments.module.css";
interface UpfrontPaymentsProps {
    selectedFollowUp?: FollowUpType;
    onSelectFollowUp: (selectedFollowUp: FollowUpType) => void;
    onCompletePayment: (
        hasValidCreditCard: boolean,
        resolvedFollowUpType: FollowUpType
    ) => Promise<void> | void;
}

type PublicCardPaymentProps = UpfrontPaymentsProps & {
    isBusy?: boolean;
};

function PublicCardPayment({
    selectedFollowUp,
    onSelectFollowUp,
    onCompletePayment,
    isBusy = false,
}: PublicCardPaymentProps): JSX.Element {
    const [authUser] = useAuthUser();
    const { translations } = useLanguage();
    const cardInfo = useCardPlaceholder();
    const getCardTokenCallback = useCardToken();
    const [analytics] = useAnalytics();
    const tUpfrontPayments = translations.upfrontPaymentSection;

    const isUserCardAvailable = !cardInfo.loading && !!cardInfo.result;
    const isExpressFollowUp = selectedFollowUp === FollowUpType.Express;

    const [isCardFieldValid, setIsCardFieldValid] = useState(
        isUserCardAvailable
    );
    useEffect(() => {
        // Update card field validity whenever async cardInfo loads
        // This ensures that the button's disabled state correctly reflects
        // whether a saved card is available after the API response returns
        if (isUserCardAvailable) {
            setIsCardFieldValid(true);
        }
    }, [isUserCardAvailable]);

    const handleSubmitPayment = useAsyncCallback(async () => {
        if (!authUser || !authUser.lastUserQuestionnaire) {
            return;
        }

        const nextFollowUpType = selectedFollowUp ?? FollowUpType.Regular;
        if (!selectedFollowUp) {
            onSelectFollowUp(nextFollowUpType);
        }

        const cardToken = await getCardTokenCallback();
        if (cardToken) {
            await Axios.put(ENDPOINTS.USER_PAYMENT_INFO, {
                token: cardToken,
            });
        }
        if (cardToken || isUserCardAvailable) {
            const method = cardToken
                ? ScreeningPaymentType.CreditCard
                : ScreeningPaymentType.CardOnFile;
            trackScreeningPaymentSubmitted(analytics, method);
            await onCompletePayment(true, nextFollowUpType);
        } else {
            setIsCardFieldValid(false);
        }
    });

    const handleSkipPayment = async () => {
        if (!handleSubmitPayment.loading && !isBusy) {
            const nextFollowUpType = FollowUpType.Regular;
            trackScreeningPaymentSubmitted(
                analytics,
                ScreeningPaymentType.Cash
            );
            onSelectFollowUp(nextFollowUpType);
            await onCompletePayment(false, nextFollowUpType);
        }
    };

    let buttonText = tUpfrontPayments.paymentForm.confirmButtonText;
    if (isExpressFollowUp) {
        buttonText = `${tUpfrontPayments.paymentForm.payButtonPrefix} 55$`;
    }

    return (
        <>
            <PaymentFieldComponent
                loading={cardInfo.loading}
                setIsValid={setIsCardFieldValid}
                existingCardInfo={cardInfo.loading ? null : cardInfo.result}
            />

            <Button
                loading={handleSubmitPayment.loading || isBusy}
                onClick={() => handleSubmitPayment.execute()}
                disabled={!isCardFieldValid || !selectedFollowUp || isBusy}
            >
                {buttonText}
            </Button>

            {selectedFollowUp !== FollowUpType.Express && (
                <p
                    className={`${styles.upfrontPaymentsSection__text} ${styles["upfrontPaymentsSection__text--underline"]}`}
                >
                    <a onClick={handleSkipPayment}>
                        {tUpfrontPayments.public.cashPaymentInClinic}
                    </a>
                </p>
            )}
        </>
    );
}

export default function UpfrontPayments({
    selectedFollowUp,
    onSelectFollowUp,
    onCompletePayment,
}: UpfrontPaymentsProps): JSX.Element {
    const isBookingFlowRedesign = useBookingFlowRedesign();
    const { translations, language } = useLanguage();
    const [analytics] = useAnalytics();
    const tUpfrontPayments = translations.upfrontPaymentSection;
    const appointmentHold = getAppointmentHold();
    const storedFollowUpType = getBookingFollowUpType();
    const resolvedFollowUpType = isBookingFlowRedesign
        ? storedFollowUpType ?? selectedFollowUp ?? FollowUpType.Regular
        : selectedFollowUp;
    const isExpressFollowUp = resolvedFollowUpType === FollowUpType.Express;
    const [isWalletProcessing, setIsWalletProcessing] = useState(false);

    const handleWalletPaid = async (method: string) => {
        const nextFollowUpType = resolvedFollowUpType ?? FollowUpType.Express;
        if (!resolvedFollowUpType) {
            onSelectFollowUp(nextFollowUpType);
        }
        trackScreeningPaymentSubmitted(analytics, method);
        await onCompletePayment(true, nextFollowUpType);
    };

    const transportFeeAmount =
        resolvedFollowUpType === FollowUpType.Express
            ? tUpfrontPayments.public.expressTransportFeeAmount
            : tUpfrontPayments.public.transportFeeAmount;

    const useReservationSubtitle =
        isBookingFlowRedesign && resolvedFollowUpType === FollowUpType.Regular;
    const tBookingPayment = translations.bookingFlow.payment;

    const serviceHeading =
        buildReservedAppointmentDescription(
            appointmentHold?.datetime,
            appointmentHold?.clinicName,
            language,
            useReservationSubtitle
                ? tBookingPayment.reservationSubtitle
                : translations.upfrontPaymentSection.description
        ) ??
        (useReservationSubtitle
            ? tBookingPayment.reservationSubtitleFallback
            : translations.upfrontPaymentSection.descriptionFallback);

    const paymentDetails = (
        <>
            <PaymentMethodsBusy
                busy={isWalletProcessing}
                label={tUpfrontPayments.paymentForm.walletProcessing}
            >
                {isExpressFollowUp && (
                    <ExpressWalletCheckout
                        flow={WalletCheckoutFlow.ExpressPayPublic}
                        onPaid={handleWalletPaid}
                        onProcessingChange={setIsWalletProcessing}
                    />
                )}

                <CardElementsProvider>
                    <PublicCardPayment
                        selectedFollowUp={resolvedFollowUpType}
                        onSelectFollowUp={onSelectFollowUp}
                        onCompletePayment={onCompletePayment}
                        isBusy={isWalletProcessing}
                    />
                </CardElementsProvider>
            </PaymentMethodsBusy>

            <p
                className={`${styles.upfrontPaymentsSection__text} ${styles["upfrontPaymentsSection__text--bold"]}`}
            >
                {`${tUpfrontPayments.public.transportFeeMessage} ${transportFeeAmount}`}
            </p>

            <BookingFlowPaymentFooterDisclaimers variant="legacy" />
        </>
    );

    if (isBookingFlowRedesign && resolvedFollowUpType) {
        const tPublic = tUpfrontPayments.public;
        const dueSummary = getPublicBookingFlowDueSummary(
            resolvedFollowUpType,
            language,
            tPublic
        );

        return (
            <BookingFlowNarrowContent className={styles.upfrontPaymentsSection}>
                <BookingFlowPageHeading
                    title={tUpfrontPayments.alternateSectionTitle}
                    subtitle={serviceHeading}
                    size="large"
                    subtitleFullWidth
                    tightSubtitle
                />
                <BookingFlowScreeningPayment
                    dueSummary={dueSummary}
                    walletCheckoutFlow={WalletCheckoutFlow.ExpressPayPublic}
                    paymentFollowUpType={resolvedFollowUpType}
                    onSelectFollowUp={onSelectFollowUp}
                    onCompletePayment={onCompletePayment}
                />
            </BookingFlowNarrowContent>
        );
    }

    return (
        <div className={`container--narrow ${styles.upfrontPaymentsSection}`}>
            <h2>{tUpfrontPayments.alternateSectionTitle}</h2>
            <div className={styles.upfrontPaymentsSection__appointmentSummary}>
                {serviceHeading}
            </div>

            <div className={styles.upfrontPaymentsSection__turnaroundTime}>
                {tUpfrontPayments.resultsTurnaroundTimeHeading}
            </div>

            <ServiceSelect
                selectedFollowUp={selectedFollowUp}
                onSelectFollowUp={onSelectFollowUp}
                disabled={isWalletProcessing}
            />

            {paymentDetails}
        </div>
    );
}
